import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import { bitmapToImageData } from "@brickme/project-core/src/bitmap/image-data.ts";
import { renderPictureSync } from "@brickme/project-core/src/render/render-picture.ts";
import { runGeneratorSync } from "@brickme/project-core/src/utils/run-generator.ts";
import type { PictureConfiguration } from "@brickme/project-core/src/model/picture.ts";
import renderPictureAsPixels from "@brickme/project-core/src/render/render-picture-as-pixels.ts";
import renderBaseplateOutlines from "@brickme/project-core/src/render/render-baseplate-outlines.ts";
import type { RenderRunner, RenderSource } from "./render-runner-type.ts";
import { ensureExhaustive } from "../utils/never.ts";
import renderSource from "./render-source.ts";

// TODO: Find a better system for preventing unnecessary re-renders.
type CurrentRender = {
	readonly type: RenderSource["type"];
	// readonly drawBaseplates: boolean;
	// readonly picture: PictureConfiguration | undefined;
	// readonly build: BuildBitmap | undefined;
	// Not aborting any more
	// readonly abortController: AbortController;
};

type Options = {
	// In main thread will want timeout. But slower, so in worker prefer microtask
	readonly pendingRenderMethod: "timeout" | "microtask";
};

function createRenderRunner(
	_: Options,
): RenderRunner<HTMLCanvasElement | OffscreenCanvas> {
	let performingRender: CurrentRender | undefined;
	let canvasCtx:
		| (Parameters<typeof renderPictureSync>[0] & {
				readonly canvas: {
					width: number;
					height: number;
				};
		  })
		| undefined;
	let utilityCanvasCtx: Parameters<typeof renderPictureSync>[2] | undefined;
	let canvasSourceImage: HTMLCanvasElement | OffscreenCanvas | undefined;
	let source: RenderSource | undefined;
	let canvasSize: GenericSize<number> | undefined;
	let hasPendingRender = false;
	let drawBaseplates = false;
	// TODO: Rendering twice. Once on picture change, even though some render configurations
	// don't require it.
	let picture: PictureConfiguration | undefined;

	function tryRender() {
		if (
			!canvasCtx ||
			!utilityCanvasCtx ||
			!source ||
			!canvasSize ||
			!canvasSourceImage ||
			!picture
		) {
			return;
		}

		// We let any current render continue to give quicker user feedback.
		// then pick up pending renders later. This solves the case of say scrubbing a slider.
		// If the slider emits an event every 10ms and render takes 15ms then we'll never see
		// a render until the user stops moving the slider.
		// currentRender.abortController.abort();
		if (performingRender) {
			hasPendingRender = true;
			return;
		}

		const myRender = {
			type: source.type,
			// drawBaseplates,
			// build: source.type === "bricked" ? source.build : undefined,
			// picture: drawBaseplates || source.type === "source" ? picture : undefined
		};
		performingRender = myRender;
		// Setting width or height (even if the same) results in a canvas clear
		if (canvasCtx.canvas.width !== canvasSize.width) {
			canvasCtx.canvas.width = canvasSize.width;
		}
		if (canvasCtx.canvas.height !== canvasSize.height) {
			canvasCtx.canvas.height = canvasSize.height;
		}

		if (import.meta.env.MODE === "development") {
			// eslint-disable-next-line no-console
			console.time(`Render (${myRender.type})`);
		}

		// Draw main image
		switch (source.type) {
			case "bricked":
				renderPictureSync(canvasCtx, source.build, utilityCanvasCtx);
				break;
			case "solid":
				runGeneratorSync(
					renderPictureAsPixels(canvasCtx, utilityCanvasCtx, source.build),
				);
				break;
			case "source":
				renderSource(canvasCtx, canvasSourceImage, picture);
				break;
			default:
				ensureExhaustive(source);
		}

		// Draw baseplates
		if (drawBaseplates) {
			renderBaseplateOutlines(
				canvasCtx as CanvasRenderingContext2D,
				picture.numberOfBasePlates,
				canvasCtx.canvas.width,
				canvasCtx.canvas.height,
			);
		}

		if (import.meta.env.MODE === "development") {
			// if (myRender.abortController.signal.aborted) {
			// 	console.log("Rendering ABORTED");
			// } else
			// eslint-disable-next-line no-console
			console.timeEnd(`Render (${myRender.type})`);
		}

		if (performingRender === myRender) {
			performingRender = undefined;
		}

		if (hasPendingRender) {
			hasPendingRender = false;
			// Use next frame instead of direct call to avoid stack overflows
			tryRenderNextFrame();
		}
	}

	let nextFrameRenderId: ReturnType<typeof setTimeout> | undefined;
	function tryRenderNextFrame() {
		// queueMicrotask happens too quick. No time to cancel before redundant call comes in.
		// This is part of multiple safeguards to try and prevent too many renders.
		// if (pendingRenderMethod === "microtask") {
		// queueMicrotask(() => void tryRender());
		// } else {
		if (nextFrameRenderId !== undefined) {
			clearTimeout(nextFrameRenderId);
		}
		nextFrameRenderId = setTimeout(tryRender);
	}

	return {
		setSourceImage(
			sourceImage: Bitmap,
			utilityCanvas: HTMLCanvasElement | OffscreenCanvas,
		) {
			/* eslint-disable no-param-reassign */
			utilityCanvas.width = sourceImage.width;
			utilityCanvas.height = sourceImage.height;
			/* eslint-enable no-param-reassign */
			canvasSourceImage = utilityCanvas;
			const sourceImageCtx = utilityCanvas.getContext("2d", {
				alpha: false,
			});
			if (sourceImageCtx === null) {
				throw new Error("Failed to get context");
			}
			sourceImageCtx.putImageData(bitmapToImageData(sourceImage), 0, 0);
			tryRenderNextFrame();
		},
		setCanvas(newCanvas: HTMLCanvasElement | OffscreenCanvas) {
			const newCtx = newCanvas.getContext("2d", {
				alpha: false,
			});
			if (newCtx === null) {
				throw new Error("Canvas ctx not working");
			}
			canvasCtx = newCtx;
			tryRenderNextFrame();
		},
		setSource(newSource: RenderSource | undefined) {
			source = newSource;
			tryRenderNextFrame();
		},
		setDrawBaseplates(newDrawBaseplates: boolean) {
			if (newDrawBaseplates !== drawBaseplates) {
				drawBaseplates = newDrawBaseplates;
				tryRenderNextFrame();
			}
		},
		setPictureConfiguration(newPicture: PictureConfiguration) {
			picture = newPicture;
			// Only source renders or base plates need picture config
			if (source?.type === "source" || drawBaseplates) {
				tryRenderNextFrame();
			}
		},
		setUtilityCanvas(
			newUtilityCanvas: HTMLCanvasElement | OffscreenCanvas,
			newCanvasSize: GenericSize<number>,
		) {
			canvasSize = newCanvasSize;
			const newCtx = newUtilityCanvas.getContext("2d", {
				alpha: false,
			});
			if (newCtx === null) {
				throw new Error("Utility ctx not working");
			}
			utilityCanvasCtx = newCtx;
			tryRenderNextFrame();
		},
		clearUtilityCanvas() {
			canvasSize = undefined;
			utilityCanvasCtx = undefined;
			tryRenderNextFrame();
		},
		terminate() {
			// Nothing
		},
	};
}

export { createRenderRunner };
